import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'scratch-vm';

import RecordModal from './record-modal.jsx';
import VisionTrainerModalComponent from '../components/vision-trainer-modal/vision-trainer-modal.jsx';
import {getVisionClassifierSession} from '../lib/vision/vision-classifier-session';

const TRAINING_STEPS = [
    'Jeg kigger på dine billeder...',
    'Jeg leder efter ting, der ligner hinanden...',
    'Jeg øver mig på klasserne...',
    'Næsten klar!',
    'Klar til at gætte!'
];

class VisionTrainerModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleAddLabel',
            'handleActiveInputTypeChange',
            'handleCancelClassTypeChoice',
            'handleCreateAudioLabel',
            'handleCreateImageLabel',
            'handleClearTraining',
            'handleCloseAudioRecorder',
            'handleDraftLabelChange',
            'handleDraftLabelKeyDown',
            'handleRemoveExample',
            'handleRemoveLabel',
            'handleSessionUpdate',
            'handleStartAddingExamples',
            'handleStopAddingExamples',
            'handleSubmitAudioRecording',
            'handleTogglePrediction',
            'handleUploadExamples',
            'stopTrainingProgress',
            'setVideoElement'
        ]);

        this.session = getVisionClassifierSession(this.props.vm);
        this.state = {
            ...this.session.getState(),
            draftLabel: '',
            audioRecordingLabel: null,
            classTypeChoiceVisible: false,
            trainingProgress: 0,
            trainingProgressMessage: '',
            trainingProgressVisible: false
        };
        this.trainingProgressTimeouts = [];
    }

    async componentDidMount () {
        this.unsubscribe = this.session.subscribe(this.handleSessionUpdate);
        await this.session.startPreview();
        this.attachVideoStream();
    }

    componentDidUpdate () {
        this.attachVideoStream();
    }

    componentWillUnmount () {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
        this.session.stopAddingExamples();
        this.stopTrainingProgress();
        this.session.closePreview();
    }

    setVideoElement (element) {
        this.videoElement = element;
        this.attachVideoStream();
    }

    attachVideoStream () {
        if (!this.videoElement) {
            return;
        }

        if (this.session.stream && this.videoElement.srcObject !== this.session.stream) {
            this.videoElement.srcObject = this.session.stream;
        }

        if (!this.session.stream && this.videoElement.srcObject) {
            this.videoElement.srcObject = null;
        }
    }

    handleSessionUpdate (nextState) {
        this.setState(currentState => ({
            ...currentState,
            ...nextState
        }));
    }

    handleDraftLabelChange (event) {
        this.setState({
            draftLabel: event.target.value
        });
    }

    handleDraftLabelKeyDown (event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.handleAddLabel();
        }
    }

    handleAddLabel () {
        if (!this.state.draftLabel.trim()) {
            this.session.addLabel(this.state.draftLabel);
            return;
        }
        this.setState({
            classTypeChoiceVisible: true
        });
    }

    handleCreateImageLabel () {
        this.createLabelWithType('image');
    }

    handleCreateAudioLabel () {
        this.createLabelWithType('audio');
    }

    handleCancelClassTypeChoice () {
        this.setState({
            classTypeChoiceVisible: false
        });
    }

    createLabelWithType (type) {
        if (this.session.addLabel(this.state.draftLabel, type)) {
            this.setState({
                draftLabel: '',
                classTypeChoiceVisible: false
            });
        }
    }

    handleActiveInputTypeChange (event) {
        this.session.setActiveInputType(event.target.value);
    }

    handleStartAddingExamples (label) {
        if (this.session.getLabelType(label) === 'audio') {
            this.setState({
                audioRecordingLabel: label
            });
            return;
        }
        this.session.startAddingExamples(label);
    }

    handleStopAddingExamples () {
        this.session.stopAddingExamples();
    }

    handleCloseAudioRecorder () {
        this.setState({
            audioRecordingLabel: null
        });
    }

    handleSubmitAudioRecording (samples, sampleRate) {
        this.session.addRecordedAudioExample(this.state.audioRecordingLabel, samples, sampleRate);
    }

    handleRemoveLabel (label) {
        this.session.removeLabel(label);
    }

    handleRemoveExample (label, exampleId) {
        this.session.removeExample(label, exampleId);
    }

    stopTrainingProgress () {
        this.trainingProgressTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
        this.trainingProgressTimeouts = [];
        this.setState({
            trainingProgress: 0,
            trainingProgressMessage: '',
            trainingProgressVisible: false
        });
    }

    handleTogglePrediction () {
        if (this.state.trainingProgressVisible) {
            return;
        }
        if (this.state.isPredicting) {
            this.session.togglePrediction();
            return;
        }
        if (!this.state.hasLabels || !this.state.hasExamples || this.state.isInitializing) {
            return;
        }

        this.trainingProgressTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
        this.trainingProgressTimeouts = [];
        this.setState({
            trainingProgress: 0,
            trainingProgressMessage: TRAINING_STEPS[0],
            trainingProgressVisible: true
        });

        TRAINING_STEPS.forEach((message, index) => {
            const timeoutId = setTimeout(() => {
                this.setState({
                    trainingProgress: Math.round(((index + 1) / TRAINING_STEPS.length) * 100),
                    trainingProgressMessage: message
                });
            }, index * 650);
            this.trainingProgressTimeouts.push(timeoutId);
        });

        const startPredictionTimeout = setTimeout(async () => {
            this.trainingProgressTimeouts = [];
            await this.session.startPrediction();
            this.setState({
                trainingProgress: 100,
                trainingProgressMessage: TRAINING_STEPS[TRAINING_STEPS.length - 1]
            });

            const hideTimeout = setTimeout(() => {
                this.setState({
                    trainingProgress: 0,
                    trainingProgressMessage: '',
                    trainingProgressVisible: false
                });
            }, 500);
            this.trainingProgressTimeouts.push(hideTimeout);
        }, TRAINING_STEPS.length * 650);
        this.trainingProgressTimeouts.push(startPredictionTimeout);
    }

    handleClearTraining () {
        this.session.clearTraining();
    }

    handleUploadExamples (label, event) {
        const files = event.target.files;
        this.session.addUploadedExamples(label, files);
        event.target.value = '';
    }

    render () {
        return (
            <React.Fragment>
                <VisionTrainerModalComponent
                    {...this.state}
                    onActiveInputTypeChange={this.handleActiveInputTypeChange}
                    onAddLabel={this.handleAddLabel}
                    onCancelClassTypeChoice={this.handleCancelClassTypeChoice}
                    onClearTraining={this.handleClearTraining}
                    onCreateAudioLabel={this.handleCreateAudioLabel}
                    onCreateImageLabel={this.handleCreateImageLabel}
                    onDraftLabelChange={this.handleDraftLabelChange}
                    onDraftLabelKeyDown={this.handleDraftLabelKeyDown}
                    onRemoveExample={this.handleRemoveExample}
                    onRemoveLabel={this.handleRemoveLabel}
                    onRequestClose={this.props.onRequestClose}
                    onStartAddingExamples={this.handleStartAddingExamples}
                    onStopAddingExamples={this.handleStopAddingExamples}
                    onTogglePrediction={this.handleTogglePrediction}
                    onUploadExamples={this.handleUploadExamples}
                    videoRef={this.setVideoElement}
                    vm={this.props.vm}
                />
                {this.state.audioRecordingLabel ? (
                    <RecordModal
                        onClose={this.handleCloseAudioRecorder}
                        onSubmitSamples={this.handleSubmitAudioRecording}
                    />
                ) : null}
            </React.Fragment>
        );
    }
}

VisionTrainerModal.propTypes = {
    onRequestClose: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

export default VisionTrainerModal;
